import * as React from 'react';
import { Button } from './Button';
import { FileNameButton, FilePreviewWindow } from './FilePreviewWindow';
import { downloadFile } from '../utils/DownloadFile';
import { DxfFileIcon, ImageFileIcon, PdfFileIcon } from './FileTypeIcons';
import {
  DrawingListItem,
  ImageListItem,
  ImageListItemState,
  PreviewTarget,
  getDrawingRowState,
} from '../utils/AttachedFileList.logic';
import './AttachedFileList.css';

// 図面の登録・編集の画面と図枠の登録・編集の画面で共通に使う、添付したファイルの一覧と拡大表示の部品

const THUMBNAIL_CLASS_NAME = 'AttachedFileList-image_thumbnail';
const PLACEHOLDER_CLASS_NAME = 'AttachedFileList-image_thumbnail AttachedFileList-image_placeholder';

function getRowClassName(state: ImageListItemState): string {
  if (state === 'pending') {
    return 'AttachedFileList-image_item AttachedFileList-image_item_pending';
  }
  if (state === 'deleted') {
    return 'AttachedFileList-image_item AttachedFileList-image_item_deleted';
  }
  return 'AttachedFileList-image_item';
}

interface IPreviewThumbnailProps {
  src: string;
  fileName: string;
  thumbnailReady: boolean;
  onPreview: () => void;
}

// 画像の縮小表示。まだ読み込んでいないアップロード済み画像は画像の印を出し、クリックで拡大表示する
const PreviewThumbnail: React.FC<IPreviewThumbnailProps> = function (props) {
  return (
    <button
      type="button"
      className="AttachedFileList-image_preview_button"
      title="クリックで拡大表示"
      onClick={props.onPreview}
    >
      {props.thumbnailReady && (
        <img className={THUMBNAIL_CLASS_NAME} src={props.src} alt={props.fileName} />
      )}
      {!props.thumbnailReady && <ImageFileIcon className={PLACEHOLDER_CLASS_NAME} />}
    </button>
  );
};

interface IStateLabelProps {
  state: ImageListItemState;
}

const StateLabel: React.FC<IStateLabelProps> = function (props) {
  if (props.state === 'pending') {
    return (
      <span className="AttachedFileList-image_state_label AttachedFileList-image_state_label_pending">
        追加予定
      </span>
    );
  }
  if (props.state === 'deleted') {
    return (
      <span className="AttachedFileList-image_state_label AttachedFileList-image_state_label_deleted">
        削除予定
      </span>
    );
  }
  return null;
};

interface IFileRejectMessageProps {
  message: string;
}

// 形式や容量で添付できなかったファイルの知らせ。ダイアログで画面をふさがず、ファイルを入れた追加欄のすぐ下に赤い小さな文字で出す
export const FileRejectMessage: React.FC<IFileRejectMessageProps> = function (props) {
  if (props.message === '') {
    return null;
  }
  return (
    <p className="AttachedFileList-file_error" role="alert">
      {props.message}
    </p>
  );
};

interface IImageListRowProps {
  item: ImageListItem;
  disabled: boolean;
  onPreview: (item: ImageListItem) => void;
  onRemove: (item: ImageListItem) => void;
  onRestore: (item: ImageListItem) => void;
}

const ImageListRow: React.FC<IImageListRowProps> = function (props) {
  const isDeleted = props.item.state === 'deleted';

  return (
    <div className={getRowClassName(props.item.state)}>
      <div className="AttachedFileList-image_preview_cell">
        <PreviewThumbnail
          src={props.item.previewSrc}
          fileName={props.item.fileName}
          thumbnailReady={props.item.thumbnailReady}
          onPreview={() => props.onPreview(props.item)}
        />
      </div>
      <div className="AttachedFileList-image_name_cell">
        <FileNameButton fileName={props.item.fileName} onClick={() => props.onPreview(props.item)} />
        <StateLabel state={props.item.state} />
      </div>
      <div className="AttachedFileList-image_action_cell">
        {!isDeleted && (
          <Button
            type="button"
            color="red"
            mini={true}
            disabled={props.disabled}
            onClick={() => props.onRemove(props.item)}
          >
            削除
          </Button>
        )}
        {isDeleted && (
          <Button
            type="button"
            color="white"
            mini={true}
            disabled={props.disabled}
            onClick={() => props.onRestore(props.item)}
          >
            取り消し
          </Button>
        )}
      </div>
    </div>
  );
};

interface IAttachedImageListProps {
  items: ImageListItem[];
  disabled: boolean;
  onPreview: (item: ImageListItem) => void;
  onRemove: (item: ImageListItem) => void;
  onRestore: (item: ImageListItem) => void;
}

// 関連する画像の一覧。見出しの行は固定し、行の部分だけを縦にスクロールする。画像が無いときは何も出さない
export const AttachedImageList: React.FC<IAttachedImageListProps> = function (props) {
  if (props.items.length === 0) {
    return null;
  }
  return (
    <div className="AttachedFileList-image_list_container">
      <div className="AttachedFileList-image_list_header">
        <div className="AttachedFileList-image_preview_cell">プレビュー</div>
        <div className="AttachedFileList-image_name_cell">ファイル名</div>
        <div className="AttachedFileList-image_action_cell">操作</div>
      </div>
      <div className="AttachedFileList-image_list_body">
        {props.items.map((item) => (
          <ImageListRow
            key={item.key}
            item={item}
            disabled={props.disabled}
            onPreview={props.onPreview}
            onRemove={props.onRemove}
            onRestore={props.onRestore}
          />
        ))}
      </div>
    </div>
  );
};

interface IAttachedDrawingProps {
  item: DrawingListItem;
  disabled: boolean;
  onPreview: (item: DrawingListItem) => void;
  onDownload: (item: DrawingListItem) => void;
  onRemove: () => void;
}

// 図面や図枠のファイルの1行。画像形式のファイルだけ縮小表示と拡大表示ができる
export const AttachedDrawing: React.FC<IAttachedDrawingProps> = function (props) {
  const state = getDrawingRowState(props.item.isPending);
  // 名前を押すと、画像の図面は拡大表示し、DXFとPDFの図面はそのままダウンロードする
  const openDrawing = function () {
    if (props.item.kind === 'image') {
      props.onPreview(props.item);
      return;
    }
    props.onDownload(props.item);
  };

  return (
    <div className="AttachedFileList-image_list_container">
      <div className="AttachedFileList-image_list_body">
        <div className={getRowClassName(state)}>
          <div className="AttachedFileList-image_preview_cell">
            {props.item.kind === 'dxf' && <DxfFileIcon className={PLACEHOLDER_CLASS_NAME} />}
            {props.item.kind === 'pdf' && <PdfFileIcon className={PLACEHOLDER_CLASS_NAME} />}
            {props.item.kind === 'image' && (
              <PreviewThumbnail
                src={props.item.previewSrc}
                fileName={props.item.fileName}
                thumbnailReady={props.item.thumbnailReady}
                onPreview={() => props.onPreview(props.item)}
              />
            )}
          </div>
          <div className="AttachedFileList-image_name_cell">
            <FileNameButton fileName={props.item.fileName} onClick={openDrawing} />
            <StateLabel state={state} />
          </div>
          <div className="AttachedFileList-image_action_cell">
            <Button
              type="button"
              color="red"
              mini={true}
              disabled={props.disabled}
              onClick={props.onRemove}
            >
              削除
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};

export interface FilePreview {
  target: PreviewTarget | null;
  open: (target: PreviewTarget) => void;
  close: () => void;
  handleLoaded: () => void;
}

/**
 * 拡大表示の状態。onFirstLoaded は、まだ縮小表示を出していないファイルを拡大表示で初めて読み終えたときに呼び、
 * 呼び出し側はそのファイルの一覧の縮小表示を実画像に切り替える
 */
export function useFilePreview(onFirstLoaded: (target: PreviewTarget) => void): FilePreview {
  const [target, setTarget] = React.useState<PreviewTarget | null>(null);

  return {
    target,
    open(nextTarget: PreviewTarget) {
      setTarget(nextTarget);
    },
    close() {
      setTarget(null);
    },
    handleLoaded() {
      if (target === null || target.thumbnailReady) {
        return;
      }
      onFirstLoaded(target);
    },
  };
}

interface IFilePreviewDialogProps {
  preview: FilePreview;
}

// 画像の拡大表示の窓。拡大表示の対象が無いときは何も出さない。窓の［ダウンロード］は、拡大表示しているファイルを保存する
export const FilePreviewDialog: React.FC<IFilePreviewDialogProps> = function (props) {
  const target = props.preview.target;
  if (target === null) {
    return null;
  }
  return (
    <FilePreviewWindow
      fileName={target.fileName}
      src={target.src}
      onClose={props.preview.close}
      onDownload={() => downloadFile(target.downloadUrl, target.fileName)}
      onLoad={props.preview.handleLoaded}
    />
  );
};
